import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { buildApiUrl } from "../config/api";
import "./PaymentStatus.css";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clearCart } = useCart();

  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);

  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError("Stripe session ID is missing.");
      setLoading(false);
      return;
    }

    let intervalId;
    const maxAttempts = 15;

    const verifySession = async () => {
      try {
        const response = await fetch(buildApiUrl(`/payments/session/${sessionId}`));
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "Could not verify payment.");
        }

        const currentSession = data.session;
        setSession(currentSession);

        if (currentSession.paymentStatus === "paid") {
          // Clear the cart only after webhook fulfillment confirms order is paid (Requirement 7)
          if (!clearedRef.current) {
            clearCart();
            clearedRef.current = true;
          }
          setLoading(false);
          clearInterval(intervalId);
        } else if (currentSession.paymentStatus === "failed" || currentSession.paymentStatus === "cancelled") {
          setError("This payment session has failed or was cancelled.");
          setLoading(false);
          clearInterval(intervalId);
        } else {
          // Still pending, increment attempts
          setAttempts((prev) => {
            const nextAttempts = prev + 1;
            if (nextAttempts >= maxAttempts) {
              clearInterval(intervalId);
              setLoading(false);
            }
            return nextAttempts;
          });
        }
      } catch (err) {
        setError(err.message || "Could not verify payment.");
        setLoading(false);
        clearInterval(intervalId);
      }
    };

    // Run first check immediately
    verifySession();

    // Start polling every 3 seconds (Requirement 8)
    intervalId = setInterval(verifySession, 3000);

    return () => clearInterval(intervalId);
  }, [sessionId, clearCart]);

  const downloads = session?.downloads || [];
  const isPaid = session?.paymentStatus === "paid";
  const isPending = session && session.paymentStatus !== "paid" && !error;

  return (
    <main className="payment-status-page">
      <section className="payment-status-card">
        <p className="eyebrow">Payment Status</p>

        {isPaid && <h1>Thank you for your order.</h1>}
        {isPending && <h1>Payment is processing...</h1>}
        {loading && !session && <h1>Verifying payment...</h1>}
        {error && <h1>Verification failed</h1>}

        {loading && (
          <p style={{ marginTop: "10px" }}>
            {isPending
              ? "Waiting for payment confirmation from Stripe (handling secure fulfillment)..."
              : `Verifying your Stripe payment and preparing your download link... (Attempt ${attempts + 1}/15)`}
          </p>
        )}

        {error && <div className="payment-error">{error}</div>}

        {session && (
          <div className="payment-summary">
            <p><strong>Payment Status:</strong> {session.paymentStatus}</p>
            <p><strong>Email:</strong> {session.customerEmail || "Not available"}</p>
            <p><strong>Order ID:</strong> {session.orderId || "Not available"}</p>
            <p><strong>Total:</strong> {((session.amountTotal || 0) / 100).toFixed(2)} {String(session.currency || "USD").toUpperCase()}</p>
          </div>
        )}

        {isPaid && (
          <div className="download-box">
            <h2>Your Downloads</h2>

            {downloads.length > 0 ? (
              downloads.map((download) => (
                <div className="download-item" key={download.token}>
                  <div>
                    <strong>{download.productTitle}</strong>
                    <span>
                      {download.downloadCount}/{download.maxDownloads} downloads used · Expires {new Date(download.expiresAt).toLocaleDateString()}
                    </span>
                  </div>

                  {download.hasFile ? (
                    <a
                      href={buildApiUrl(`/downloads/file/${download.token}`)}
                      className="download-button"
                    >
                      Download File
                    </a>
                  ) : (
                    <span className="download-pending">
                      Private file not attached yet
                    </span>
                  )}
                </div>
              ))
            ) : (
              <p>No download token was created yet. Please refresh this page.</p>
            )}
          </div>
        )}

        {isPending && !loading && (
          <div className="download-box" style={{ background: "rgba(228, 176, 31, 0.08)", borderColor: "rgba(228, 176, 31, 0.2)" }}>
            <h2>Payment Processing</h2>
            <p>
              Your payment is processing via a delayed payment method. Once Stripe confirms your payment,
              your download links will be generated automatically and sent to your email.
            </p>
          </div>
        )}

        <p className="payment-note" style={{ marginTop: "20px" }}>
          Download access is available immediately after payment confirmation. Links are limited and expire for security.
        </p>

        <div className="payment-actions">
          <Link to="/shop">Continue Shopping</Link>
          <Link to="/">Back Home</Link>
        </div>
      </section>
    </main>
  );
};

export default PaymentSuccess;
